import { sendGoogleEvent } from "../features/cookie-consent/google-analytics";
import { sendPixelEvent } from "../features/cookie-consent/meta-pixel";
import {
  registerSuperProperties,
  sendPostHogEvent,
  unregisterSuperProperty,
} from "@/features/cookie-consent/posthog-analytics";
import { Cart } from "./use-cart";
import { NotificationType } from "../types";

export const useTracker = () => {
  return {
    // Reservation events
    trackAddToCart,
    trackAddToCartForAdditionalProduct,
    trackPurchase,
    trackInitiateCheckout,
    trackCitySelectionPage,
    trackSaleSelectionPage,
    trackProductsSelectionPage,
    trackGoToReservationCheckoutBtnClick,
    trackReservationLoginModalView,
    trackProductCategoryNameView,
    trackProductCategoriesView,
    registerSelectedCity,
    registerSelectedLocation,
    unregisterReservationFlowSuperProperties,
    trackNotificationSettingChange,
    trackProductCategoryFilterClick,

    // Closed sales reminder events
    trackClosedSalesReminderCtaClick,
    trackClosedSalesReminderLoginModal,
    trackClosedSalesReminderLoginSuccess,
    trackClosedSalesReminderEmailSubscribed,
    trackClosedSalesReminderEmailUnsubscribed,
    trackClosedSalesReminderSmsSubscribed,
    trackClosedSalesReminderSmsUnsubscribed,

    // Grocery events
    trackProductsSelectionPageGrocery,
    trackAddToCartGrocery,
    trackGoToGroceryCheckoutBtnClick,
    trackGroceryLoginModalView,
    trackInitiateCheckoutGrocery,
    trackPurchaseGrocery,
  };
};

const currency = (window as any).mangasPortal === 'pl' ? 'PLN' : 'EUR';

const registerSelectedCity = (city: string): void => {
  registerSuperProperties({ selected_city: city });
};

const registerSelectedLocation = (id: number, title: string): void => {
  registerSuperProperties({
    selected_location_id: id,
    selected_location_name: title,
  });
};

const unregisterReservationFlowSuperProperties = (): void => {
  unregisterSuperProperty("selected_city");
  unregisterSuperProperty("selected_location_id");
  unregisterSuperProperty("selected_location_name");
};

const trackCitySelectionPage = (): void => {
  sendGoogleEvent("city_selection_page_view");
  sendPostHogEvent("city_selection_page_view");
};

const trackSaleSelectionPage = (): void => {
  sendGoogleEvent("sale_selection_page_view");
  sendPostHogEvent("sale_selection_page_view");
};

const trackProductsSelectionPage = (): void => {
  sendGoogleEvent("product_selection_page_view");
  sendPostHogEvent("product_selection_page_view");
};

const trackGoToReservationCheckoutBtnClick = (): void => {
  sendGoogleEvent("go_to_reservation_checkout_btn_click");
  sendPostHogEvent("go_to_reservation_checkout_btn_click");
};

const trackReservationLoginModalView = (): void => {
  sendGoogleEvent("reservation_login_modal_page_view");
  sendPostHogEvent("reservation_login_modal_page_view");
};

const trackProductCategoryNameView = (name: string): void => {
  sendPostHogEvent("category_name_view", { category: name });
};

const trackProductCategoriesView = (
  categories: { name: string; percentageSeen: number }[]
): void => {
  categories.forEach((category) => {
    sendPostHogEvent("category_view_percentage", {
      category: category.name,
      percentage_seen: category.percentageSeen,
    });
  });
};

const trackPurchase = (
  productPriceSum: number,
  products: Cart["items"]["value"],
  reservationId: number
): void => {
  sendPixelEvent("Purchase", {
    value: productPriceSum,
    currency,
  });
  sendGoogleEvent("purchase", {
    value: productPriceSum,
    currency,
    transaction_id: reservationId,
    items: products.map((item) => ({
      item_name: item.product.title,
      currency,
      price: item.product.price,
      quantity: item.quantity,
    })),
  });
  sendPostHogEvent("purchase", {
    value: productPriceSum,
    currency,
    transaction_id: reservationId,
    items: products.map((item) => ({
      item_id: item.product.id,
      item_name: item.product.title,
      currency,
      price: item.product.price,
      quantity: item.quantity,
      category: item.product.category_name,
    })),
  });
};

const trackInitiateCheckout = (
  productPriceSum: number,
  products: Cart["items"]["value"]
): void => {
  sendPixelEvent("InitiateCheckout", {
    currency,
    value: productPriceSum,
  });
  sendGoogleEvent("begin_checkout", {
    currency,
    value: productPriceSum,
    items: products.map((item) => ({
      item_name: item.product.title,
      currency,
      price: item.product.price,
      quantity: item.quantity,
    })),
  });
  sendPostHogEvent("begin_checkout", {
    currency,
    value: productPriceSum,
    items: products.map((item) => ({
      item_name: item.product.title,
      currency,
      price: item.product.price,
      quantity: item.quantity,
    })),
  });
};

const trackAddToCart = (title: string, price: number): void => {
  sendPixelEvent("AddToCart", {
    content_name: title,
    value: price,
    currency,
  });
  sendGoogleEvent("add_to_cart", {
    value: price,
    currency,
    items: [
      {
        item_name: title,
        currency,
        price: price,
      },
    ],
  });
  sendPostHogEvent("add_to_cart");
};

const trackAddToCartForAdditionalProduct = (
  productId: number,
  title: string,
  price: number
): void => {
  sendPostHogEvent("add_to_cart_additional_product", {
    item_id: productId,
    item_name: title,
    currency,
    price: price,
  });
};

const trackNotificationSettingChange = (
  type: NotificationType,
  isTurnedOn: boolean,
  origin: "profile" | "reservationSuccess" | "grocerySuccess"
) => {
  sendPostHogEvent("notification_settings_change", {
    type,
    subscribed: isTurnedOn,
    origin,
  });
};

const trackProductCategoryFilterClick = (category: string): void => {
  sendPostHogEvent("product_category_filter_click", { category });
};

// Closed sales reminder events

const trackClosedSalesReminderCtaClick = (): void => {
  sendPostHogEvent("closed_sales_reminder_cta_click");
};

const trackClosedSalesReminderLoginModal = (): void => {
  sendPostHogEvent("closed_sales_reminder_login_modal");
};

const trackClosedSalesReminderLoginSuccess = (): void => {
  sendPostHogEvent("closed_sales_reminder_login_success");
};


const trackClosedSalesReminderEmailSubscribed = (): void => {
  sendPostHogEvent("closed_sales_reminder_email_subscribed");
};

const trackClosedSalesReminderEmailUnsubscribed = (): void => {
  sendPostHogEvent("closed_sales_reminder_email_unsubscribed");
};

const trackClosedSalesReminderSmsSubscribed = (): void => {
  sendPostHogEvent("closed_sales_reminder_sms_subscribed");
};

const trackClosedSalesReminderSmsUnsubscribed = (): void => {
  sendPostHogEvent("closed_sales_reminder_sms_unsubscribed");
};

// Grocery events

const trackProductsSelectionPageGrocery = (): void => {
  sendPostHogEvent("product_selection_page_view_grocery");
};

const trackAddToCartGrocery = (title: string, price: number): void => {
  sendPostHogEvent("add_to_cart_grocery");
};

const trackGoToGroceryCheckoutBtnClick = (): void => {
  sendPostHogEvent("go_to_grocery_checkout_btn_click");
};

const trackGroceryLoginModalView = (): void => {
  sendPostHogEvent("grocery_login_modal_page_view");
};

const trackInitiateCheckoutGrocery = (
  productPriceSum: number,
  products: Cart["items"]["value"]
): void => {
  sendPostHogEvent("begin_checkout_grocery", {
    currency,
    value: productPriceSum,
    items: products.map((item) => ({
      item_name: item.product.title,
      currency,
      price: item.product.price,
      quantity: item.quantity,
    })),
  });
};

const trackPurchaseGrocery = (
  productPriceSum: number,
  products: Cart["items"]["value"],
  reservationId: number
): void => {
  sendPostHogEvent("purchase_grocery", {
    value: productPriceSum,
    currency,
    transaction_id: reservationId,
    items: products.map((item) => ({
      item_name: item.product.title,
      currency,
      price: item.product.price,
      quantity: item.quantity,
      category: item.product.category_name,
    })),
  });
  sendPixelEvent("purchase_grocery", {
    value: productPriceSum,
    currency,
    transaction_id: reservationId,
  });
  sendGoogleEvent("purchase_grocery", {
    value: productPriceSum,
    currency,
    transaction_id: reservationId,
  });  
};
